import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getIdea, incrementViews, resolveIdea, voteIdea, deleteIdea, updateIdea } from '../services/ideas';
import { getComments, createComment, voteComment, deleteComment } from '../services/comments';
import { getLivePriceForIdea, isKalshiConfigured, addKalshiReferral } from '../services/kalshi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

const IdeaDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  
  const [idea, setIdea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeForm, setCloseForm] = useState({
    outcome: 'win',
    actualPrice: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  // Live price state
  const [livePrice, setLivePrice] = useState(null);
  const [livePriceLoading, setLivePriceLoading] = useState(false);
  const [livePriceError, setLivePriceError] = useState(null);

  useEffect(() => {
    fetchIdea();
    fetchComments();
  }, [id]);

  useEffect(() => {
    fetchLivePrice();

    // Refresh live price every 30 seconds
    const interval = setInterval(() => {
      fetchLivePrice();
    }, 30000);

    return () => clearInterval(interval);
  }, [idea]);

  const fetchIdea = async () => {
    try {
      setLoading(true);
      const fetchedIdea = await getIdea(id);
      setIdea(fetchedIdea);
      
      // Increment view count
      await incrementViews(id);
    } catch (error) {
      console.error('Error fetching idea:', error);
      toast.error('Failed to load idea');
      navigate('/ideas');
    } finally {
      setLoading(false);
    }
  };

  const fetchLivePrice = async () => {
    if (!idea || !isKalshiConfigured()) {
      return;
    }

    // Only fetch for Kalshi active ideas
    if (idea.platform !== 'Kalshi' || idea.status !== 'active') {
      return;
    }

    try {
      setLivePriceLoading(true);
      setLivePriceError(null);
      
      const price = await getLivePriceForIdea(idea);
      setLivePrice(price);
    } catch (error) {
      console.error('Error fetching live price:', error);
      setLivePriceError(error.message);
    } finally {
      setLivePriceLoading(false);
    }
  };

  const handleVote = async (value) => {
    if (!currentUser) {
      toast.error('Please login to vote');
      return;
    }

    try {
      await voteIdea(id, value);
      // Update local state
      setIdea(prev => ({
        ...prev,
        votes: (prev.votes || 0) + value
      }));
      toast.success(value > 0 ? 'Upvoted!' : 'Downvoted!');
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to vote');
    }
  };

  const calculateROI = (entryPrice, actualPrice, position) => {
    if (!entryPrice || !actualPrice) return 0;
    
    let roi = 0;
    if (position === 'Yes' || position === 'Long') {
      roi = ((actualPrice - entryPrice) / entryPrice) * 100;
    } else if (position === 'No' || position === 'Short') {
      roi = ((entryPrice - actualPrice) / entryPrice) * 100;
    }
    
    return roi.toFixed(2);
  };

  const handleClosePosition = async (e) => {
    e.preventDefault();
    
    if (!closeForm.actualPrice || isNaN(closeForm.actualPrice)) {
      toast.error('Please enter a valid actual price');
      return;
    }

    setSubmitting(true);

    try {
      const actualPrice = parseFloat(closeForm.actualPrice);
      const roi = parseFloat(calculateROI(idea.entryPrice, actualPrice, idea.position));
      
      await resolveIdea(id, {
        outcome: closeForm.outcome,
        actualPrice,
        roi,
        verificationStatus: 'pending',
        verifiedBy: null,
        verifiedAt: null,
      });

      toast.success('Position closed successfully!');
      setShowCloseModal(false);
      
      // Refresh idea data
      await fetchIdea();
    } catch (error) {
      console.error('Error closing position:', error);
      toast.error('Failed to close position');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this idea? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteIdea(id);
      toast.success('Idea deleted successfully');
      navigate('/ideas');
    } catch (error) {
      console.error('Error deleting idea:', error);
      toast.error('Failed to delete idea');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'low':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-blue-100 text-blue-700';
      case 'resolved':
        return 'bg-green-100 text-green-700';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getROIColor = (roi) => {
    if (roi > 0) return 'text-green-600';
    if (roi < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  // Comments functions
  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      const fetchedComments = await getComments(id);
      setComments(fetchedComments);
    } catch (error) {
      console.error('Error fetching comments:', error);
      // Don't show error toast for comments, just log it
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleCreateComment = async (e) => {
    e.preventDefault();
    
    if (!currentUser) {
      toast.error('Please login to comment');
      return;
    }

    if (!commentText.trim()) {
      toast.error('Comment cannot be empty');
      return;
    }

    if (commentText.trim().length < 10) {
      toast.error('Comment must be at least 10 characters');
      return;
    }

    setCommentSubmitting(true);

    try {
      const commentData = {
        text: commentText.trim(),
        authorId: currentUser.uid,
        authorName: currentUser.displayName || 'Anonymous',
        authorEmail: currentUser.email,
      };

      await createComment(id, commentData);
      
      // Update idea comment count
      await updateIdea(id, {
        comments: (idea.comments || 0) + 1
      });

      // Refresh comments
      await fetchComments();
      
      // Clear form
      setCommentText('');
      
      toast.success('Comment posted!');
    } catch (error) {
      console.error('Error creating comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleCommentVote = async (commentId, value) => {
    if (!currentUser) {
      toast.error('Please login to vote');
      return;
    }

    try {
      await voteComment(commentId, value);
      
      // Update local state
      setComments(prevComments =>
        prevComments.map(comment =>
          comment.id === commentId
            ? { ...comment, votes: (comment.votes || 0) + value }
            : comment
        )
      );
      
      toast.success(value > 0 ? 'Upvoted!' : 'Downvoted!');
    } catch (error) {
      console.error('Error voting on comment:', error);
      toast.error('Failed to vote');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await deleteComment(commentId);
      
      // Update idea comment count
      await updateIdea(id, {
        comments: Math.max(0, (idea.comments || 1) - 1)
      });

      // Refresh comments
      await fetchComments();
      
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const isAuthor = currentUser && idea && currentUser.uid === idea.authorId;
  const canClosePosition = isAuthor && idea?.status === 'active';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!idea) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h2 className="text-2xl font-bold text-gray-900">Idea Not Found</h2>
          <button
            onClick={() => navigate('/ideas')}
            className="mt-4 text-primary-600 hover:text-primary-700"
          >
            ← Back to Ideas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/ideas')}
          className="flex items-center text-gray-600 hover:text-primary-600 mb-6 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Ideas
        </button>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Header Section */}
          <div className="p-8 border-b border-gray-200">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div className="flex gap-3">
                <span className="px-4 py-2 text-sm font-medium bg-primary-100 text-primary-700 rounded-full">
                  {idea.platform}
                </span>
                <span className={`px-4 py-2 text-sm font-medium rounded-full ${getStatusColor(idea.status)}`}>
                  {idea.status?.toUpperCase()}
                </span>
                <span className={`px-3 py-2 text-sm font-medium rounded border ${getRiskLevelColor(idea.riskLevel)}`}>
                  {idea.riskLevel} Risk
                </span>
              </div>

              {/* Author Actions */}
              {isAuthor && (
                <div className="flex gap-2">
                  {canClosePosition && (
                    <button
                      onClick={() => setShowCloseModal(true)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm"
                    >
                      Close Position
                    </button>
                  )}
                  <button
                    onClick={handleDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {idea.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span>By{' '}
                  <button
                    onClick={() => navigate(`/users/${idea.authorId}`)}
                    className="font-medium text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                  >
                    {idea.authorName}
                  </button>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                <span>{formatDate(idea.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                </svg>
                <span>{idea.views || 0} views</span>
              </div>
            </div>
          </div>

          {/* Description Section */}
          <div className="p-8 border-b border-gray-200 bg-gray-50">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">Overview</h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
              {idea.description}
            </p>
          </div>

          {/* Live Price Section (Kalshi only) - Only show if we have valid live data */}
          {idea.platform === 'Kalshi' && idea.status === 'active' && isKalshiConfigured() && livePrice && !livePriceError && (
            <div className="p-8 border-b border-gray-200 bg-gradient-to-br from-blue-50 to-indigo-50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold text-gray-900">Live Market Data</h2>
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-100 rounded-full">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-semibold text-green-700 uppercase">Live</span>
                  </div>
                </div>
                {livePrice.lastUpdated && (
                  <span className="text-sm text-gray-500">
                    Updated {new Date(livePrice.lastUpdated).toLocaleTimeString()}
                  </span>
                )}
              </div>

              {livePrice.isClosed ? (
                <div className="p-6 bg-gray-100 border border-gray-300 rounded-lg">
                  <div className="text-center">
                    <p className="text-lg font-semibold text-gray-700 mb-2">Market Closed</p>
                    {livePrice.settlementPrice !== null && (
                      <div>
                        <p className="text-sm text-gray-600 mb-1">Settlement Price</p>
                        <p className="text-3xl font-bold text-gray-900">{livePrice.settlementPrice}¢</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    {/* Current Price */}
                    <div className="bg-white rounded-lg p-6 shadow-md border-2 border-blue-200">
                      <p className="text-sm text-blue-600 font-medium mb-2 uppercase tracking-wide">Current Price</p>
                      <p className="text-4xl font-bold text-blue-900 mb-2">
                        {livePrice.currentPrice !== null ? `${livePrice.currentPrice.toFixed(1)}¢` : 'N/A'}
                      </p>
                      {(() => {
                        const priceChange = livePrice.currentPrice !== null && idea.entryPrice
                          ? ((livePrice.currentPrice - idea.entryPrice) / idea.entryPrice * 100)
                          : null;
                        
                        if (priceChange === null) return null;
                        
                        return (
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-semibold ${
                              priceChange >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {priceChange >= 0 ? '▲' : '▼'} {Math.abs(priceChange).toFixed(2)}%
                            </span>
                            <span className="text-xs text-gray-500">from entry</span>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Bid Price */}
                    <div className="bg-white rounded-lg p-6 shadow-md">
                      <p className="text-sm text-gray-500 font-medium mb-2">Best Bid</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {livePrice.bid !== null ? `${livePrice.bid}¢` : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">Highest buy price</p>
                    </div>

                    {/* Ask Price */}
                    <div className="bg-white rounded-lg p-6 shadow-md">
                      <p className="text-sm text-gray-500 font-medium mb-2">Best Ask</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {livePrice.ask !== null ? `${livePrice.ask}¢` : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">Lowest sell price</p>
                    </div>
                  </div>

                  {/* Additional Market Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {livePrice.lastPrice !== null && (
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-xs text-gray-500 mb-1">Last Trade</p>
                        <p className="text-lg font-semibold text-gray-900">{livePrice.lastPrice}¢</p>
                      </div>
                    )}
                    {livePrice.volume !== undefined && (
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-xs text-gray-500 mb-1">Volume</p>
                        <p className="text-lg font-semibold text-gray-900">{livePrice.volume.toLocaleString()}</p>
                      </div>
                    )}
                    {livePrice.openInterest !== undefined && (
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-xs text-gray-500 mb-1">Open Interest</p>
                        <p className="text-lg font-semibold text-gray-900">{livePrice.openInterest.toLocaleString()}</p>
                      </div>
                    )}
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-1">Status</p>
                      <p className="text-lg font-semibold text-green-600 capitalize">{livePrice.status || 'Active'}</p>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                    <p className="text-xs text-blue-800">
                      <strong>Note:</strong> Prices update automatically every 30 seconds. For the most up-to-date information, visit the market directly.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Trade Details Grid */}
          <div className="p-8 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Trade Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Position</p>
                <p className="text-lg font-semibold text-gray-900">{idea.position}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Entry Price</p>
                <p className="text-lg font-semibold text-gray-900">{idea.entryPrice}¢</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-green-600 mb-1">Target Price</p>
                <p className="text-lg font-semibold text-green-700">{idea.targetPrice}¢</p>
              </div>
              {idea.stopLoss && (
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-sm text-red-600 mb-1">Stop Loss</p>
                  <p className="text-lg font-semibold text-red-700">{idea.stopLoss}¢</p>
                </div>
              )}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Expiration Date</p>
                <p className="text-lg font-semibold text-gray-900">
                  {idea.marketExpirationDate ? new Date(idea.marketExpirationDate).toLocaleDateString('en-US', { 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }) : 'N/A'}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-1">Risk Level</p>
                <p className="text-lg font-semibold text-gray-900">{idea.riskLevel}</p>
              </div>
            </div>

            {/* Market URL */}
            {idea.marketUrl && (
              <div className="mt-6">
                <a
                  href={idea.platform === 'Kalshi' ? addKalshiReferral(idea.marketUrl) : idea.marketUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  View on {idea.platform}
                </a>
              </div>
            )}
          </div>

          {/* Investment Reasoning */}
          <div className="p-8 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Investment Reasoning</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {idea.reasoning}
              </p>
            </div>
          </div>

          {/* Resolution Details (if resolved) */}
          {idea.status === 'resolved' && (
            <div className="p-8 border-b border-gray-200 bg-gradient-to-br from-primary-50 to-white">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Resolution</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-500 mb-1">Outcome</p>
                  <p className={`text-lg font-semibold ${idea.outcome === 'win' ? 'text-green-600' : 'text-red-600'}`}>
                    {idea.outcome === 'win' ? '✓ Win' : '✗ Loss'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-500 mb-1">Actual Exit Price</p>
                  <p className="text-lg font-semibold text-gray-900">{idea.actualPrice}¢</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-sm text-gray-500 mb-1">Return on Investment</p>
                  <p className={`text-2xl font-bold ${getROIColor(idea.roi)}`}>
                    {idea.roi > 0 ? '+' : ''}{idea.roi}%
                  </p>
                </div>
              </div>
              {idea.resolvedAt && (
                <p className="mt-4 text-sm text-gray-600">
                  Closed on {formatDate(idea.resolvedAt)}
                </p>
              )}
            </div>
          )}

          {/* Tags */}
          {idea.tags && idea.tags.length > 0 && (
            <div className="p-8 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {idea.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Voting Section */}
          <div className="p-8 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => handleVote(1)}
                  disabled={!currentUser}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                  </svg>
                  <span className="font-medium">{idea.votes || 0}</span>
                </button>
                <button
                  onClick={() => handleVote(-1)}
                  disabled={!currentUser}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:border-red-500 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                  </svg>
                </button>
              </div>
              <div className="text-sm text-gray-500">
                {!currentUser && 'Login to vote'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Close Position Modal */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Close Position</h3>
            
            <form onSubmit={handleClosePosition}>
              {/* Outcome */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Outcome <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCloseForm(prev => ({ ...prev, outcome: 'win' }))}
                    className={`px-4 py-3 rounded-lg font-medium transition-all ${
                      closeForm.outcome === 'win'
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ✓ Win
                  </button>
                  <button
                    type="button"
                    onClick={() => setCloseForm(prev => ({ ...prev, outcome: 'loss' }))}
                    className={`px-4 py-3 rounded-lg font-medium transition-all ${
                      closeForm.outcome === 'loss'
                        ? 'bg-red-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    ✗ Loss
                  </button>
                </div>
              </div>

              {/* Actual Price */}
              <div className="mb-6">
                <label htmlFor="actualPrice" className="block text-sm font-medium text-gray-700 mb-2">
                  Actual Exit Price (¢) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="actualPrice"
                  value={closeForm.actualPrice}
                  onChange={(e) => setCloseForm(prev => ({ ...prev, actualPrice: e.target.value }))}
                  placeholder="Enter actual price"
                  min="0"
                  max="100"
                  step="0.01"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* ROI Preview */}
              {closeForm.actualPrice && !isNaN(closeForm.actualPrice) && (
                <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg">
                  <p className="text-sm text-primary-600 font-medium mb-1">Calculated ROI</p>
                  <p className={`text-2xl font-bold ${getROIColor(parseFloat(calculateROI(idea.entryPrice, parseFloat(closeForm.actualPrice), idea.position)))}`}>
                    {calculateROI(idea.entryPrice, parseFloat(closeForm.actualPrice), idea.position) > 0 ? '+' : ''}
                    {calculateROI(idea.entryPrice, parseFloat(closeForm.actualPrice), idea.position)}%
                  </p>
                </div>
              )}

              {/* Notes */}
              <div className="mb-6">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  value={closeForm.notes}
                  onChange={(e) => setCloseForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  placeholder="Add any additional notes about closing this position..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? 'Closing...' : 'Close Position'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCloseModal(false)}
                  disabled={submitting}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Comments Section */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Comments Header */}
          <div className="p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="text-2xl font-bold text-gray-900">
              Discussion ({comments.length})
            </h2>
          </div>

          {/* Comment Form */}
          {currentUser ? (
            userProfile?.onboardingStatus === 'pending' ? (
              <div className="p-6 border-b border-gray-200 bg-yellow-50 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <p className="text-yellow-900 font-medium">Commenting Locked</p>
                </div>
                <p className="text-sm text-yellow-800 mb-3">
                  Submit and get your first idea approved to unlock commenting!
                </p>
                <button
                  onClick={() => navigate('/submit-idea')}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium text-sm"
                >
                  Submit Your First Idea
                </button>
              </div>
            ) : (
              <div className="p-6 border-b border-gray-200">
                <form onSubmit={handleCreateComment}>
                  <div className="mb-4">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Share your thoughts on this idea... (minimum 10 characters)"
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                    />
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm text-gray-500">
                        {commentText.length} characters
                      </span>
                      <button
                        type="submit"
                        disabled={commentSubmitting || commentText.trim().length < 10}
                        className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
                      >
                        {commentSubmitting ? 'Posting...' : 'Post Comment'}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )
          ) : (
            <div className="p-6 border-b border-gray-200 bg-gray-50 text-center">
              <p className="text-gray-600">
                Please <button onClick={() => navigate('/login')} className="text-primary-600 hover:text-primary-700 font-medium">login</button> to join the discussion
              </p>
            </div>
          )}

          {/* Comments List */}
          <div className="divide-y divide-gray-200">
            {commentsLoading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-4 text-gray-500">Loading comments...</p>
              </div>
            ) : comments.length === 0 ? (
              <div className="p-12 text-center">
                <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">No comments yet</h3>
                <p className="mt-2 text-gray-500">Be the first to share your thoughts!</p>
              </div>
            ) : (
              comments.map((comment) => (
                <div key={comment.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex gap-4">
                    {/* Author Avatar */}
                    <div 
                      className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => navigate(`/users/${comment.authorId}`)}
                    >
                      <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                        {comment.authorName?.charAt(0).toUpperCase() || 'A'}
                      </div>
                    </div>

                    {/* Comment Content */}
                    <div className="flex-1 min-w-0">
                      {/* Author and Date */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => navigate(`/users/${comment.authorId}`)}
                            className="font-semibold text-primary-600 hover:text-primary-700 hover:underline transition-colors"
                          >
                            {comment.authorName || 'Anonymous'}
                          </button>
                          <span className="text-sm text-gray-500">
                            {formatDate(comment.createdAt)}
                          </span>
                        </div>
                        
                        {/* Delete Button (for comment author) */}
                        {currentUser && currentUser.uid === comment.authorId && (
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            className="text-sm text-red-600 hover:text-red-700 font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </div>

                      {/* Comment Text */}
                      <p className="text-gray-700 leading-relaxed mb-3 whitespace-pre-wrap">
                        {comment.text}
                      </p>

                      {/* Voting Buttons */}
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleCommentVote(comment.id, 1)}
                          disabled={!currentUser}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                          </svg>
                          <span className="font-medium">{comment.votes || 0}</span>
                        </button>
                        
                        <button
                          onClick={() => handleCommentVote(comment.id, -1)}
                          disabled={!currentUser}
                          className="flex items-center gap-1 px-3 py-1 text-sm bg-gray-100 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.105-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                          </svg>
                        </button>

                        {!currentUser && (
                          <span className="text-xs text-gray-500">Login to vote</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default IdeaDetailPage;

